import { ArrowRight, Bot, Clock } from "lucide-react";
import type { FlowConnector, FlowStep } from "@/types";

interface FlowStepTreeProps {
  steps: FlowStep[];
  connectors?: FlowConnector[];
}

function getStepLabel(step: FlowStep): string {
  return (step.config?.label as string) || step.agentName || "Step";
}

function StepBadge({ step }: Readonly<{ step: FlowStep }>) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-surface-secondary rounded-lg border border-border-secondary min-w-max">
      <Bot className="h-4 w-4 text-content-tertiary" />
      <span className="text-sm text-content-primary">{getStepLabel(step)}</span>
      {step.requiresReview && <Clock className="h-3.5 w-3.5 text-yellow-400" />}
    </div>
  );
}

function StepTree({ step, childrenOf }: Readonly<{ step: FlowStep; childrenOf: Map<number | null, FlowStep[]> }>) {
  const children = childrenOf.get(step.id) ?? [];
  return (
    <div className="flex flex-col items-center">
      <StepBadge step={step} />
      {children.length > 0 && (
        <>
          <div className="w-px h-4 bg-border-secondary" />
          <div className={`flex gap-0 items-start ${children.length > 1 ? "border-t border-border-secondary" : ""}`}>
            {children.map((child) => (
              <div key={child.id} className="flex flex-col items-center px-3">
                <div className="w-px h-4 bg-border-secondary" />
                <StepTree step={child} childrenOf={childrenOf} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function FlowStepTree({ steps, connectors = [] }: Readonly<FlowStepTreeProps>) {
  if (steps.length === 0) {
    return <p className="text-content-tertiary text-sm">No steps configured</p>;
  }

  // No connectors — render linear chain
  if (connectors.length === 0) {
    return (
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center gap-2">
            <StepBadge step={step} />
            {index < steps.length - 1 && <ArrowRight className="h-4 w-4 text-content-quaternary shrink-0" />}
          </div>
        ))}
      </div>
    );
  }

  // Build tree from connectors
  const childrenOf = new Map<number | null, FlowStep[]>();
  const hasParent = new Set<number>();

  for (const conn of connectors) {
    const parent = conn.fromStepId ?? null;
    if (!childrenOf.has(parent)) childrenOf.set(parent, []);
    const child = steps.find((s) => s.id === conn.toStepId);
    if (child) {
      childrenOf.get(parent)!.push(child);
      hasParent.add(conn.toStepId);
    }
  }

  const roots = steps.filter((s) => !hasParent.has(s.id));

  return (
    <div className="flex gap-6 justify-center overflow-x-auto pb-2">
      {roots.map((root) => (
        <StepTree key={root.id} step={root} childrenOf={childrenOf} />
      ))}
    </div>
  );
}
